import { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

interface LocationState {
  jobTitle?: string;
  contractorName?: string;
  completedAt?: string;
}

function StarRating({
  value,
  onChange,
  size = 32,
}: {
  value: number;
  onChange: (v: number) => void;
  size?: number;
}) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 2, fontSize: size, lineHeight: 1,
            color: star <= (hover || value) ? '#f59e0b' : '#d1d5db',
          }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

const OVERALL_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

export function LeaveReview() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;

  const [overallRating, setOverallRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [timelinessRating, setTimelinessRating] = useState(0);
  const [priceFairnessRating, setPriceFairnessRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [improvementText, setImprovementText] = useState('');
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/reviews/${jobId}`, {
        overall_rating:        overallRating,
        quality_rating:        qualityRating   || undefined,
        communication_rating:  communicationRating || undefined,
        timeliness_rating:     timelinessRating || undefined,
        price_fairness_rating: priceFairnessRating || undefined,
        review_text:           reviewText   || undefined,
        improvement_text:      improvementText || undefined,
        photo_urls:            [],
      }),
    onSuccess: () => {
      navigate('/app/reviews/confirmation', {
        replace: true,
        state: {
          overallRating,
          contractorName: state.contractorName,
          reviewText: reviewText || null,
        },
      });
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    },
  });

  function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const incoming = Array.from(e.target.files ?? []);
    const combined = [...photoFiles, ...incoming].slice(0, 5);
    setPhotoFiles(combined);
    setPhotoPreviews(combined.map((f) => URL.createObjectURL(f)));
    e.target.value = '';
  }

  function removePhoto(idx: number) {
    URL.revokeObjectURL(photoPreviews[idx] ?? '');
    setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== idx));
  }

  const categoryRows: [string, number, (v: number) => void][] = [
    ['Quality of work',  qualityRating,       setQualityRating],
    ['Communication',    communicationRating,  setCommunicationRating],
    ['Timeliness',       timelinessRating,     setTimelinessRating],
    ['Price fairness',   priceFairnessRating,  setPriceFairnessRating],
  ];

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        background: '#1D4ED8', padding: '48px 20px 24px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            width: 36, height: 36, borderRadius: 10, cursor: 'pointer',
            fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          &#8592;
        </button>
        <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>
          Leave a Review
        </h1>
      </div>

      <div style={{ padding: '20px 16px 120px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Job summary card */}
        {(state.contractorName ?? state.jobTitle) && (
          <div style={{
            background: '#fff', borderRadius: 16, padding: 16,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            <p style={{ margin: 0, fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Reviewing
            </p>
            <p style={{ margin: '4px 0 0', fontWeight: 700, fontSize: 16, color: '#111827' }}>
              {state.contractorName ?? 'Your contractor'}
            </p>
            {state.jobTitle && (
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>{state.jobTitle}</p>
            )}
            {state.completedAt && (
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#9ca3af' }}>
                Completed{' '}
                {new Date(state.completedAt).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </p>
            )}
          </div>
        )}

        {/* Overall rating */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: 20,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center',
        }}>
          <p style={{ margin: '0 0 14px', fontWeight: 600, fontSize: 16, color: '#111827' }}>
            Overall Rating <span style={{ color: '#ef4444' }}>*</span>
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <StarRating value={overallRating} onChange={setOverallRating} size={44} />
          </div>
          <p style={{
            margin: '10px 0 0', fontSize: 14, fontWeight: 500,
            color: overallRating > 0 ? '#f59e0b' : '#d1d5db',
            minHeight: 20,
          }}>
            {OVERALL_LABELS[overallRating]}
          </p>
        </div>

        {/* Category ratings */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <p style={{ margin: '0 0 16px', fontWeight: 600, fontSize: 15, color: '#111827' }}>
            Rate by Category <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 13 }}>(optional)</span>
          </p>
          {categoryRows.map(([label, val, setter]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{label}</span>
              <StarRating value={val} onChange={setter} size={24} />
            </div>
          ))}
        </div>

        {/* Text areas */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 15, color: '#111827', marginBottom: 8 }}>
            What went well? <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 13 }}>(optional)</span>
          </label>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            maxLength={2000}
            placeholder="Tell others what the contractor did great..."
            style={{
              width: '100%', height: 96, padding: '10px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 10,
              fontSize: 14, resize: 'vertical', outline: 'none',
              fontFamily: 'inherit', boxSizing: 'border-box', color: '#111827',
            }}
          />
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af', textAlign: 'right' }}>
            {reviewText.length}/2000
          </p>

          <label style={{ display: 'block', fontWeight: 600, fontSize: 15, color: '#111827', margin: '16px 0 8px' }}>
            Any suggestions? <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 13 }}>(optional)</span>
          </label>
          <textarea
            value={improvementText}
            onChange={(e) => setImprovementText(e.target.value)}
            maxLength={1000}
            placeholder="How could they improve?"
            style={{
              width: '100%', height: 80, padding: '10px 12px',
              border: '1.5px solid #e5e7eb', borderRadius: 10,
              fontSize: 14, resize: 'vertical', outline: 'none',
              fontFamily: 'inherit', boxSizing: 'border-box', color: '#111827',
            }}
          />
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af', textAlign: 'right' }}>
            {improvementText.length}/1000
          </p>
        </div>

        {/* Photo upload */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <p style={{ margin: '0 0 12px', fontWeight: 600, fontSize: 15, color: '#111827' }}>
            Photos{' '}
            <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 13 }}>
              (optional, max 5)
            </span>
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {photoPreviews.map((url, i) => (
              <div
                key={i}
                style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', background: '#f3f4f6' }}
              >
                <img
                  src={url}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  style={{
                    position: 'absolute', top: 4, right: 4,
                    background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff',
                    width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                    fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {photoPreviews.length < 5 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  aspectRatio: '1', borderRadius: 10,
                  border: '2px dashed #d1d5db', background: '#f9fafb',
                  cursor: 'pointer', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 4, color: '#9ca3af',
                }}
              >
                <span style={{ fontSize: 24, lineHeight: 1 }}>+</span>
                <span style={{ fontSize: 11 }}>Photo</span>
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoSelect}
            style={{ display: 'none' }}
          />
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 12, padding: 14,
          }}>
            <p style={{ margin: 0, color: '#dc2626', fontSize: 14 }}>{error}</p>
          </div>
        )}
      </div>

      {/* Fixed submit bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '12px 16px 28px',
        background: '#fff', borderTop: '1px solid #f3f4f6',
      }}>
        <button
          type="button"
          onClick={() => { setError(null); mutation.mutate(); }}
          disabled={overallRating === 0 || mutation.isPending}
          style={{
            width: '100%', padding: 14,
            background: overallRating > 0 && !mutation.isPending ? '#1D4ED8' : '#e5e7eb',
            color: overallRating > 0 && !mutation.isPending ? '#fff' : '#9ca3af',
            border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 600,
            cursor: overallRating > 0 && !mutation.isPending ? 'pointer' : 'not-allowed',
          }}
        >
          {mutation.isPending ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>
    </div>
  );
}
